import { useUserStore } from "~/stores/userStore";
import { defineStore } from "pinia";
import type { Country } from "~~/shared/types/common";

export interface LocaleDefinition {
    language: string;
    country: Country;
    localeCode: string;
    domain?: string;
}

export const useLocaleStore = defineStore('localeStore', () => {
    const DEFAULT_LANGUAGE = "en";
    const DEFAULT_COUNTRY = "US";
    const DEFAULT_LOCALE_CODE = `${DEFAULT_LANGUAGE}-${DEFAULT_COUNTRY}`;
    const COOKIE_NAME = "locale";
    const userStore = useUserStore();

    // fetch locale cookie
    const localeCookie = useCookie(COOKIE_NAME, {
        secure: !import.meta.dev
    });

    const locales = ref<LocaleDefinition[]>([]);
    const locale = computed(() => {
        const code = localeCookie.value ?? userStore?.data?.locale ?? DEFAULT_LOCALE_CODE;
        const resolvedLocale = getShippableLocale(code);

        if (localeCookie.value != null && resolvedLocale && resolvedLocale.localeCode !== normalizeLocaleCode(localeCookie.value)) {
            localeCookie.value = resolvedLocale.localeCode;
        }

        return resolvedLocale;
    });

    async function fetchLocales() {
        try {
            const result = (await siteService().fetchCountries(true))?.data?.value;
            if (!!result) {
                locales.value = result.filter(country => country.shippingEnabled).map((country) => {
                    return {
                        language: DEFAULT_LANGUAGE,
                        country: country,
                        localeCode: country.localeCode,
                        domain: country.domain
                    };
                });
            }
            return result;
        } catch (err) {
            console.error('Unable to fetch locales', err);
        }
    }
    
    function normalizeLocaleCode(localeCode?: string | null) {
        return localeCode?.replace("_", "-");
    }

    function getDefaultLocale() {
        return getLocale(DEFAULT_LOCALE_CODE)
            ?? locales.value?.find(locale => locale.country?.code === DEFAULT_COUNTRY)
            ?? locales.value?.[0];
    }

    function getLocale(localeCode?: string | null) {
        const localeCodeFixed = normalizeLocaleCode(localeCode)?.toLowerCase();
        return locales.value?.find(locale =>
            localeCodeFixed === locale.localeCode?.toLowerCase() || localeCodeFixed === locale.country?.code?.toLowerCase()
        );
    }

    function getShippableLocale(localeCode?: string | null) {
        return getLocale(localeCode) ?? getDefaultLocale();
    }

    function setLocale(newLocaleCode: string) {
        const locale = getLocale(newLocaleCode);
        if (!!locale) {
            localeCookie.value = locale.localeCode;
        }
    }

    function $reset() {
        localeCookie.value = DEFAULT_LOCALE_CODE;
    }

    const isLoyaltyPoints = ref<boolean>(false);
    const isProfessional = ref<boolean>(false);
    const loyaltyPointsValue = ref<number>(0);


    async function fetchCustomer() {
        try {
            const result:any = (await siteService().getCustomer())
            if (result) {
                isLoyaltyPoints.value = result?.data?.loyaltyPointTotal > 0;
                isProfessional.value = result?.data?.professional;
            }
            return result;
        } catch (err) {
            console.error('Unable to fetch locales', err);
        }
    } 

    function getCustomer() {
       return isLoyaltyPoints.value
    }
   
    function setCustomer(value:any) {
       isLoyaltyPoints.value = value.loyaltyPointTotal > 0;
       isProfessional.value = value.professional;
       loyaltyPointsValue.value = value.loyaltyPointTotal
    }

    return {locale, locales, isLoyaltyPoints, isProfessional, loyaltyPointsValue, getCustomer, setCustomer, getLocale, getShippableLocale, setLocale, fetchLocales, $reset, fetchCustomer};
});
